import React, { useCallback, useEffect, useState, useRef } from "react";
import Layout1 from "../../DataLayouts/Layout1";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import { fetchPersonLeaveCategoryMonthlySummary } from "../../../Apis/Employee-api";
import { MAIN_URL } from "../../../Configurations/Urls";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import axios from "axios";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
  prev_page: null,
  next_page: null,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_person_leave_category_monthly_summary_id",
  sortOrder: "desc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();
  searchParams.forEach((value, key) => {
    if (key.endsWith("_operator") || key.endsWith("_logic") || !key.startsWith("filter_")) return;
    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);
    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);
    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;
      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }
      filters.push({ field, operator, value: filterValue });
    }
  });
  const search = searchParams.get("search");
  if (search && String(search).trim()) {
    filters.push({ field: "full_name", operator: "contains", value: String(search).trim() });
  }
  return filters;
};

const DEFAULT_COLUMNS = [
  {
    field: "person_name",
    label: "Employee Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "person_code",
    label: "Employee Code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "leave_category_name",
    label: "Leave Category",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "year",
    label: "Year",
    visible: true,
    width: 100,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "month",
    label: "Month",
    visible: true,
    width: 100,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "total_leaves",
    label: "Total Leaves",
    visible: true,
    width: 120,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "approved_leaves",
    label: "Approved Leaves",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "unapproved_leaves",
    label: "Unapproved Leaves",
    visible: true,
    width: 170,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "rejected_leaves",
    label: "Rejected Leaves",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

// Helper function to get month name from month number
const getMonthName = (monthNumber) => {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  return months[monthNumber - 1] || monthNumber;
};

function LeaveCategoryMonthlySummaryReportList() {
  const [summaries, setSummaries] = useState([]);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  const { id } = useParams();

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;
    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;
    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });
    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }
    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `organization_person_leave_category_monthly_summaries_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Load data via POST /filters (metadata-driven) or fallback to GET
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;
    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();
    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_person_leave_category_monthly_summary_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && String(search).trim() !== "" && { search: String(search).trim() }),
        };
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/employee-leave-category-monthly-summary/filters`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          data = result.data ?? result.leave_category_summaries ?? [];
          if (result.success && result.pagination) {
            setPaginationData({
              page: result.pagination.page ?? 1,
              pageSize: result.pagination.pageSize || result.pagination.limit || 10,
              total: result.pagination.total || 0,
              totalPages: result.pagination.totalPages || 1,
              hasNext: result.pagination.hasNext || false,
              hasPrev: result.pagination.hasPrev || false,
              prev_page: result.pagination.prev_page ?? null,
              next_page: result.pagination.next_page ?? null,
            });
          } else if (!result.success) {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 503 || response.status === 404) {
          const fallback = await fetchPersonLeaveCategoryMonthlySummary(org.organization_id);
          data = fallback?.leave_category_summaries ?? [];
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
        const mappedSummaries = (data || []).map((item) => ({
          ...item,
          id: item?.organization_person_leave_category_monthly_summary_id ?? "",
          person_name: item?.person_name ?? "-",
          person_code: item?.person_code ?? "-",
          leave_category_name: item?.leave_category_name ?? "-",
          leave_category_code: item?.leave_category_code ?? "-",
          year: item?.year ?? "",
          month: item?.month ? getMonthName(item.month) : (item?.month ?? ""),
          month_number: item?.month ?? "",
          total_leaves: item?.total_leaves ?? 0,
          approved_leaves: item?.approved_leaves ?? 0,
          unapproved_leaves: item?.unapproved_leaves ?? 0,
          rejected_leaves: item?.rejected_leaves ?? 0,
        }));
        setSummaries(mappedSummaries);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching leave category summaries:", err);
        toast.error("Failed to load leave category monthly summary report");
        setSummaries([]);
        setPaginationData(DEFAULT_PAGINATION);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    return () => fetchControllerRef.current?.abort();
  }, [org, searchParams]);

  const handleEdit = useCallback(
    (item) => {
      // Navigate to edit page if needed
      // navigate(`/attendance/leave-category-summary/edit/${item.id}`);
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      // Navigate to view page if needed
      // navigate(`/attendance/leave-category-summary/view/${item.id}`);
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback(
    (nextFilters) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        // Remove existing filter params
        Array.from(next.keys()).forEach((key) => {
          if (key.startsWith("filter_")) next.delete(key);
        });
        // Apply new filters from table (FilterBuilder format: [{ field, operator, value }])
        if (Array.isArray(nextFilters) && nextFilters.length > 0) {
          nextFilters.forEach((f) => {
            const field = f?.field;
            if (!field) return;
            const operator = f?.operator ?? "contains";
            const requiresValue = operatorRequiresValue(operator);
            const value = requiresValue && f?.value != null && f?.value !== "" ? String(f.value) : "";
            next.set(`filter_${field}`, value);
            next.set(`filter_${field}_operator`, operator);
          });
          next.set("page", "1");
        }
        return next;
      });
    },
    [setSearchParams]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Leave Category Monthly Summary Report"}
        btnName={null}
        Data={summaries}
        tableHeaders={[
          {
            name: "Person Name",
            value_key: "person_name",
            textStyle: "capitalize",
          },
          { name: "Person Code", value_key: "person_code" },
          { name: "Leave Category", value_key: "leave_category_name" },
          { name: "Year", value_key: "year" },
          { name: "Month", value_key: "month" },
          { name: "Total Leaves", value_key: "total_leaves" },
          { name: "Approved Leaves", value_key: "approved_leaves" },
          { name: "Unapproved Leaves", value_key: "unapproved_leaves" },
          { name: "Rejected Leaves", value_key: "rejected_leaves" },
        ]}
        Icons={[
          <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <CategoryIcon sx={{ color: "text.secondary" }} />,
          <DateRangeIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Leave Category Monthly Summary Report",
          "Leave Category Monthly Summary Report",
          "Add Leave Category Monthly Summary",
          "Leave Category Monthly Summary Report",
        ]}
        Route={"/attendance/leave-category-monthly-summary"}
        setData={setSummaries}
        DeleteFunc={null}
      />

      <TableDataGeneric
        tableName="organization_person_leave_category_monthly_summaries"
        primaryKey="organization_person_leave_category_monthly_summary_id"
        heading="Leave Category Monthly Summary Report"
        data={summaries}
        sortname="organization_person_leave_category_monthly_summary_id"
        showActions={false}
        Route="/attendance/leave-category-monthly-summary"
        handleShow={handleShow}
        EditFunc={handleEdit}
        onSortChange={handleSortChange}
        onFilterChange={handleFilterChange}
        token={localStorage.getItem("token")}
        configss={configColumns}
        paginationData={paginationData}
        filterModule="employee_leave_category_monthly_summaries"
        {...(tableConfig && { config: tableConfig })}
      />
    </>
  );
}

export default LeaveCategoryMonthlySummaryReportList;

